"use client";

import Link from "next/link";
import type { Route } from "next";
import type { Locale } from "@/lib/i18n/locales";
import { mandarinSiteHeaderInterface } from "@/lib/i18n/site-header-interface-copy";
import { isPublishedMandarinPath } from "@/lib/i18n/publication";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import {
  PILOT_CONTENT_REGISTRY,
  type CounterpartResult,
} from "@/lib/i18n/content-registry";

export default function LanguageControl({
  locale = "en",
}: {
  locale?: Locale;
}) {
  const pathname = usePathname();
  const [publicPath, setPublicPath] = useState(pathname);

  useEffect(() => {
    setPublicPath(window.location.pathname);
  }, [pathname]);

  const normalizedPath =
    publicPath
      .replace(/^\/en(?=\/|$)/, "")
      .replace(/^\/zh-Hans(?=\/|$)/, "/zh") || "/";
  const isMandarin =
    normalizedPath === "/zh" || normalizedPath.startsWith("/zh/");
  const englishCopy = {
    "language.ariaLabel": "Website language",
    "language.unpublished": "Chinese translation is not published yet",
    "language.unregistered": "This page is not available in Chinese",
    "language.unavailable": "(unavailable)",
  };
  const copy =
    locale === "zh-Hans" || isMandarin
      ? mandarinSiteHeaderInterface(englishCopy)
      : englishCopy;
  let counterpart: CounterpartResult | null = null;
  try {
    counterpart = PILOT_CONTENT_REGISTRY.resolveCounterpart(
      normalizedPath,
      isMandarin ? "en" : "zh-Hans"
    );
  } catch {
    // Invalid or internally rewritten paths must never crash a header.
  }

  const chineseHref =
    counterpart?.kind === "available" ? counterpart.href : null;
  const registered =
    counterpart?.kind !== "unregistered" && counterpart !== null;

  const englishHref = isMandarin
    ? counterpart?.kind === "available"
      ? counterpart.href
      : counterpart?.kind === "unavailable"
      ? counterpart.englishPath
      : null
    : null;

  return (
    <div
      aria-label={copy["language.ariaLabel"]}
      className="flex items-center gap-2 px-2 text-sm"
    >
      {isMandarin ? (
        englishHref ? (
          <Link<string> href={englishHref as Route} hrefLang="en" lang="en">
            English
          </Link>
        ) : (
          <span lang="en" aria-disabled="true">
            English
          </span>
        )
      ) : (
        <span lang="en" aria-current="true">
          English
        </span>
      )}
      <span aria-hidden="true">/</span>
      {isMandarin ? (
        <span lang="zh-Hans" aria-current="true">
          简体中文
        </span>
      ) : chineseHref && isPublishedMandarinPath(chineseHref) ? (
        <Link<string> href={chineseHref as Route} hrefLang="zh-Hans" lang="zh-Hans">
          简体中文
        </Link>
      ) : (
        <span
          lang="zh-Hans"
          aria-disabled="true"
          title={
            registered
              ? copy["language.unpublished"]
              : copy["language.unregistered"]
          }
        >
          简体中文 <span lang={locale}>{copy["language.unavailable"]}</span>
        </span>
      )}
    </div>
  );
}
